import { useRef, useState } from "react";
import { mediaUrl } from "@/lib/api";
import { ZoomIn } from "lucide-react";

// Hover magnifier lens + click-to-enlarge. Accurately follows object-contain letterboxing.
export function ZoomImage({ src, alt, onEnlarge, zoom = 2.5, lensSize = 190, className = "", enlargeOnImageClick = true }) {
  const imgRef = useRef(null);
  const [lens, setLens] = useState(null);

  const handleEnlarge = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onEnlarge?.();
  };

  const handleMove = (e) => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth) return;
    const rect = img.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Compute the actual displayed image box inside the element (object-contain).
    const scale = Math.min(rect.width / img.naturalWidth, rect.height / img.naturalHeight);
    const dispW = img.naturalWidth * scale;
    const dispH = img.naturalHeight * scale;
    const offX = (rect.width - dispW) / 2;
    const offY = (rect.height - dispH) / 2;

    const cx = x - offX;
    const cy = y - offY;
    if (cx < 0 || cy < 0 || cx > dispW || cy > dispH) { setLens(null); return; }

    setLens({
      left: x - lensSize / 2,
      top: y - lensSize / 2,
      bgW: dispW * zoom,
      bgH: dispH * zoom,
      bgX: lensSize / 2 - cx * zoom,
      bgY: lensSize / 2 - cy * zoom,
    });
  };

  return (
    <div className={`group relative w-full h-full ${className}`} onMouseMove={handleMove} onMouseLeave={() => setLens(null)} data-testid="zoom-image-wrap">
      <img
        ref={imgRef}
        src={mediaUrl(src)}
        alt={alt}
        className={`w-full h-full object-contain ${enlargeOnImageClick ? "cursor-zoom-in" : "cursor-pointer"}`}
        onClick={enlargeOnImageClick ? handleEnlarge : undefined}
        data-testid="zoom-image"
      />
      <button
        type="button"
        onClick={handleEnlarge}
        data-testid="enlarge-image-btn"
        className="absolute top-3 right-3 w-9 h-9 rounded-full grid place-items-center z-10 text-white shadow-md
                   opacity-100 md:opacity-0 md:group-hover:opacity-100
                   transition-all duration-200 md:translate-y-1 md:group-hover:translate-y-0 hover:scale-110 active:scale-95"
        style={{ background: "var(--brand)" }}
        aria-label="Zoom / enlarge image"
        title="Click to enlarge"
      >
        <ZoomIn className="w-[18px] h-[18px]" strokeWidth={2.4} />
      </button>
      {lens && (
        <div
          className="pointer-events-none absolute rounded-full border-2 shadow-2xl hidden md:block z-20"
          style={{
            width: lensSize,
            height: lensSize,
            left: lens.left,
            top: lens.top,
            borderColor: "var(--honey-500)",
            backgroundColor: "var(--page-bg, #fff)",
            backgroundImage: `url(${mediaUrl(src)})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: `${lens.bgW}px ${lens.bgH}px`,
            backgroundPosition: `${lens.bgX}px ${lens.bgY}px`,
          }}
        />
      )}
    </div>
  );
}
